import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { useCreateProject } from '@bymason/controller-api-hooks';
import {
  Button,
  ControllerAlert as Alert,
  ControllerModal,
  Controller,
  ControllerTextField,
  ControllerValidatedSelect,
  ControllerValidatedTextField,
  DialogActions,
  DialogContent,
  MenuItem,
  yupResolver,
  useForm,
  useWatch,
  Stack,
  Link,
  FormControlLabel,
  Checkbox,
  Box,
  Text,
} from '@bymason-private/legacy-pattern-components';
import { persistBuildWithCli, retreiveBuildWithCli } from '../../../browser-storage';

import { CreateProjectModalProps, Fields, FormData, schema, initialValues } from './types';

export { FormData as CreateProjectFormData } from './types';

export function CreateProjectModal({
  onSuccess,
  onError,
  onClose,
  controllerBuildEnabled,
  open,
  deviceList,
  existingProjectNames,
}: CreateProjectModalProps) {
  const { authToken = '', customerId = '' } = useAuth();
  const [
    createProject,
    newProject,
    createProjectError,
    createProjectBusy,
    createProjectCode,
  ] = useCreateProject(authToken);

  const [buildWithCli, setBuildWithCli] = useState(retreiveBuildWithCli());

  const {
    control,
    formState: { isValid },
    getValues,
    setValue,
    setError,
    reset,
    trigger,
  } = useForm<FormData>({
    mode: 'onChange',
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const name = useWatch({ name: 'name', control });
  const device = useWatch({ name: 'deviceFamily', control });
  const deviceFamilyApiLevels = useMemo(
    () =>
      deviceList.reduce<Record<string, number[]>>((prev, deviceBuild) => {
        const prevApis = prev[deviceBuild.familyName] || [];
        const currApis = deviceBuild.builds.map((build) => Number(build.apiLevel));
        // make sure array of unique values
        const apiLevels = Array.from(new Set([...prevApis, ...currApis])).sort((a, b) => b - a);

        return { ...prev, [deviceBuild.familyName]: apiLevels };
      }, {}),
    [deviceList]
  );

  const handleBuildOptionClick = (evt: unknown, checked: boolean) => {
    controllerBuildEnabled && setBuildWithCli(checked);
  };

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const handleCreate = useCallback(() => {
    trigger();

    if (isValid) {
      const vals = getValues();
      if (customerId) {
        createProject(vals);
      }
    }
  }, [createProject, customerId, getValues, isValid, trigger]);

  const handleSuggestionClick = (suggestion: string) => () => {
    setValue('name', suggestion, { shouldValidate: true });
  };

  useEffect(() => {
    controllerBuildEnabled && persistBuildWithCli(buildWithCli);
  }, [controllerBuildEnabled, buildWithCli]);

  useEffect(() => {
    if (device) {
      setValue('apiLevel', deviceFamilyApiLevels[device]?.[0], { shouldValidate: true });
    }
  }, [deviceFamilyApiLevels, setValue, device]);

  useEffect(() => {
    if (existingProjectNames?.includes(name)) {
      setError('name', { message: `Project with the identifier '${name}' already exists`, type: 'exists' });
    }
  }, [existingProjectNames, name, setError]);

  useEffect(() => {
    if (!createProjectBusy && newProject && onSuccess) {
      onSuccess(newProject);
    }
  }, [createProjectBusy, newProject, onSuccess]);

  useEffect(() => {
    let errorMessage = createProjectError;
    if (createProjectCode === 409) {
      errorMessage = `Unable to create project. Project name already exists`;
    }

    if (errorMessage && onError) {
      onError(errorMessage, createProjectCode);
    }
  }, [createProjectCode, createProjectError, onError]);

  return (
    <ControllerModal onClose={onClose} open={open} title='Name your project'>
      <DialogContent sx={{ overflowY: 'unset' }}>
        <Stack py={2} direction='column' spacing={3}>
          <Text fontSize={14} marginBottom={1}>
            Use a Mason project to start designing your ideal customer experience. Add a build to your project
            with your apps, media, and OS configuration. Builds can be saved and deployed to device Groups.
          </Text>
          <Controller
            control={control}
            name={Fields.Name}
            render={({ field, fieldState }) => {
              const { ref, ...otherField } = field;
              const { invalid, isDirty, error } = fieldState;
              const suggestedName = (field.value as string)
                .trim()
                .replace(/\s+/g, '-')
                .replace(/[^a-zA-Z0-9-_]/g, '_')
                .toLowerCase();

              return (
                <>
                  <ControllerTextField
                    label='Project Identifier'
                    helperText='Must only contain [a-z], [A-Z], [0-9], underscore, and dashes'
                    error={isDirty && invalid}
                    errorText={error?.message}
                    autoFocus
                    required
                    fullWidth
                    inputRef={ref}
                    {...otherField}
                  />
                  {error?.type === 'alphanumericOnly' && (
                    <Alert variant='outlined' severity='info' sx={{ width: '100%' }}>
                      {
                        <span>
                          <span>Suggesting: </span>
                          <Link onClick={handleSuggestionClick(suggestedName)}>{suggestedName}</Link>
                        </span>
                      }
                    </Alert>
                  )}
                </>
              );
            }}
          />

          <ControllerValidatedTextField
            name={Fields.Description}
            control={control}
            label='Project Description'
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
          />

          <ControllerValidatedSelect
            name={Fields.DeviceFamily}
            control={control}
            label='Device'
            placeholder='Select a device family'
            required
            fullWidth
          >
            {Object.keys(deviceFamilyApiLevels).map((familyName) => (
              <MenuItem key={familyName} value={familyName}>
                {familyName}
              </MenuItem>
            ))}
          </ControllerValidatedSelect>

          <ControllerValidatedSelect
            name={Fields.ApiLevel}
            control={control}
            label='Api Level'
            placeholder={!device ? 'Select a device first' : 'Select an api level'}
            required
            fullWidth
            disabled={!device}
          >
            {(deviceFamilyApiLevels[device] || []).map((apiLevel) => (
              <MenuItem key={`${apiLevel}`} value={apiLevel}>
                {`${apiLevel}`}
              </MenuItem>
            ))}
          </ControllerValidatedSelect>
        </Stack>
        {controllerBuildEnabled && (
          <Box width='100%' display='flex' justifyContent='flex-end' padding='0 8px'>
            <FormControlLabel
              control={<Checkbox name='gotobuild' checked={buildWithCli} onChange={handleBuildOptionClick} />}
              label='Build with CLI'
              labelPlacement='start'
              sx={(theme) => ({
                '& .MuiFormControlLabel-label': {
                  ...theme.typography.h6,
                  color: (theme) => theme.palette.grey[600],
                },
              })}
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button color='secondary' onClick={handleClose} size='medium' variant='text'>
          Cancel
        </Button>
        <Button color='primary' onClick={handleCreate} size='medium' variant='contained' disabled={!isValid}>
          Create project
        </Button>
      </DialogActions>
    </ControllerModal>
  );
}
